import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Backdrop,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { roadmapAPI } from '../api/roadmapAPI'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)

// LAP-733 (GE022): labels resolved via t() at render time
const DOMAINS = [
  { value: 'PROFESSIONAL', labelKey: 'catProfessional' },
  { value: 'FINANCE',      labelKey: 'catFinance' },
  { value: 'HEALTH',       labelKey: 'catHealth' },
  { value: 'PERSONAL',     labelKey: 'catPersonalAlt' },
  { value: 'SOCIAL',       labelKey: 'catSocial' },
  { value: 'HOME',         labelKey: 'catHomeEnv' },
  { value: 'ADMIN',        labelKey: 'catAdmin' },
  { value: 'TIME',         labelKey: 'catTime' },
  { value: 'SPIRITUAL',    labelKey: 'catSpiritual' },
  { value: 'SPORTS',       labelKey: 'catSports' },
  { value: 'LEISURE',      labelKey: 'catLeisure' },
  { value: 'FOOTPRINT',    labelKey: 'catFootprint' },
  { value: 'OTHER',        labelKey: 'catOther' },
]

const IDEA_TYPES = [
  { value: 'ACCOMPLISH', labelKey: 'ideaTypeAccomplish' },
  { value: 'BECOME',     labelKey: 'ideaTypeBecome' },
  { value: 'PROGRESS',   labelKey: 'ideaTypeProgress' },
  { value: 'EXPERIENCE', labelKey: 'ideaTypeExperience' },
]

const HORIZONS = [
  { value: 'DREAMS',       labelKey: 'horizonDreams' },
  { value: 'YEAR_CURRENT', labelKey: 'horizonYearCurrent' },
  { value: 'YEAR_NEXT',    labelKey: 'horizonYearNext' },
  { value: 'YEAR_5',       labelKey: 'horizonYear5' },
  { value: 'YEAR_10',      labelKey: 'horizonYear10' },
  { value: 'COMPLETED',    labelKey: 'horizonCompleted' },
  { value: 'ABANDONED',    labelKey: 'horizonAbandoned' },
]

const PARENT_ACTION_KEYS = {
  KEEP: 'fragmentKeepAsIs',
  ABANDONED: 'fragmentMarkAbandoned',
  COMPLETED: 'fragmentMarkCompleted',
}

const FragmentIdeaDialog = ({ open, onClose, idea, onFragmented }) => {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [fragments, setFragments] = useState([
    {
      title: '',
      domain: idea?.domain || '',
      idea_type: idea?.idea_type || '',
      description: '',
      horizon: idea?.horizon || 'DREAMS',
    },
  ])
  const [parentAction, setParentAction] = useState('KEEP') // KEEP, ABANDONED, COMPLETED
  const [loading, setLoading] = useState(false)

  const currentYear = new Date().getFullYear()

  // Reset parent action when dialog opens
  useEffect(() => {
    if (open) {
      setParentAction('KEEP')
    }
  }, [open])

  const getHorizonLabel = (horizon) => {
    const labelKey = HORIZONS.find(h => h.value === horizon)?.labelKey
    const baseLabel = labelKey ? t('roadmap.' + labelKey) : horizon
    if (horizon === 'YEAR_CURRENT') {
      return `${baseLabel} (${currentYear})`
    } else if (horizon === 'YEAR_NEXT') {
      return `${baseLabel} (${currentYear + 1})`
    } else if (horizon === 'YEAR_5') {
      return `${baseLabel} (${currentYear + 5})`
    } else if (horizon === 'YEAR_10') {
      return `${baseLabel} (${currentYear + 10})`
    }
    return baseLabel
  }

  const handleAddFragment = () => {
    setFragments([
      ...fragments,
      {
        title: '',
        domain: idea?.domain || '',
        idea_type: idea?.idea_type || '',
        description: '',
        horizon: idea?.horizon || 'DREAMS',
      },
    ])
  }

  const handleRemoveFragment = (index) => {
    if (fragments.length > 1) {
      setFragments(fragments.filter((_, i) => i !== index))
    }
  }

  const handleFragmentChange = (index, field, value) => {
    const updated = [...fragments]
    updated[index][field] = value
    setFragments(updated)
  }

  const handleSubmit = async () => {
    if (!idea) return

    // Validate all fragments have titles
    const invalidFragments = fragments.filter(f => !f.title || f.title.trim() === '')
    if (invalidFragments.length > 0) {
      toast.error(t('roadmap.errFragmentTitleRequired'))
      return
    }

    try {
      setLoading(true)
      await roadmapAPI.fragmentIdea(idea.id, fragments, parentAction)
      toast.success(t('roadmap.toastFragmentsCreated', { count: fragments.length }))
      onFragmented()
      onClose()
      // Reset fragments and parent action
      setFragments([
        {
          title: '',
          domain: idea?.domain || '',
          idea_type: idea?.idea_type || '',
          description: '',
          horizon: idea?.horizon || 'DREAMS',
        },
      ])
      setParentAction('KEEP')
    } catch (error) {
      toast.error(t('roadmap.errFragmentation'))
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  if (!idea) return null

  return (
    <>
      <Backdrop
        open={open}
        sx={{
          zIndex: (theme) => theme.zIndex.modal - 1,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backgroundImage: 'url(/small.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 29, 0.6)',
          },
        }}
      />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(156, 39, 176, 0.7) 0%, rgba(255, 152, 0, 0.7) 100%), url(/small.png)',
            backgroundSize: 'cover, cover',
            backgroundPosition: 'center, center',
            backgroundBlendMode: 'overlay, normal',
            position: 'relative',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxHeight: '90vh',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 29, 0.5)',
              zIndex: 0,
              borderRadius: '20px',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            position: 'relative',
            zIndex: 1,
            color: 'white',
            fontWeight: 600,
            fontSize: '1.25rem',
          }}
        >
          {t('roadmap.fragmentTitle')}
        </DialogTitle>
        <DialogContent
          sx={{
            position: 'relative',
            zIndex: 1,
            color: 'white',
            // Custom scrollbar styling
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.5)',
              },
            },
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.05)',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              mb: 3,
              lineHeight: 1.75,
            }}
          >
            {t('roadmap.fragmentIntro')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {fragments.map((fragment, index) => (
              <Box
                key={index}
                sx={{
                  p: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                    {t('roadmap.fragmentLabel', { num: index + 1 })}
                  </Typography>
                  {fragments.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFragment(index)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': { color: '#f44336', backgroundColor: 'rgba(244, 67, 54, 0.1)' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label={t('roadmap.fieldTitleRequired')}
                    value={fragment.title}
                    onChange={(e) => handleFragmentChange(index, 'title', e.target.value)}
                    fullWidth
                    placeholder={t('roadmap.fragmentTitlePlaceholder')}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        position: 'relative',
                        outline: 'none !important',
                        boxShadow: 'none !important',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: '4px',
                          zIndex: 0,
                        },
                        '& .MuiInputBase-input': {
                          position: 'relative',
                          zIndex: 1,
                          outline: 'none !important',
                        },
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3) !important',
                          position: 'relative',
                          zIndex: 1,
                          outline: 'none !important',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused': {
                          outline: 'none !important',
                          boxShadow: 'none !important',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3) !important',
                            outline: 'none !important',
                            boxShadow: 'none !important',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />

                  <TextField
                    label={t('roadmap.fieldDescription')}
                    value={fragment.description}
                    onChange={(e) => handleFragmentChange(index, 'description', e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={t('roadmap.fragmentDescPlaceholder')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        position: 'relative',
                        outline: 'none !important',
                        boxShadow: 'none !important',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: '4px',
                          zIndex: 0,
                        },
                        '& .MuiInputBase-input': {
                          position: 'relative',
                          zIndex: 1,
                          outline: 'none !important',
                        },
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3) !important',
                          position: 'relative',
                          zIndex: 1,
                          outline: 'none !important',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused': {
                          outline: 'none !important',
                          boxShadow: 'none !important',
                          '& fieldset': {
                            borderColor: 'rgba(255, 255, 255, 0.3) !important',
                            outline: 'none !important',
                            boxShadow: 'none !important',
                          },
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <FormControl fullWidth>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
                        {t('roadmap.fieldDomain')}
                      </Typography>
                      <Select
                        value={fragment.domain || ''}
                        onChange={(e) => handleFragmentChange(index, 'domain', e.target.value)}
                        renderValue={(selected) => {
                          const domain = DOMAINS.find(d => d.value === selected)
                          return (
                            <Typography sx={{ color: 'white' }}>
                              {domain ? t('roadmap.' + domain.labelKey) : selected}
                            </Typography>
                          )
                        }}
                        sx={{
                          color: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          borderRadius: '8px',
                          position: 'relative',
                          outline: 'none !important',
                          boxShadow: 'none !important',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: '8px',
                            zIndex: 0,
                          },
                          '& .MuiSelect-select': {
                            position: 'relative',
                            zIndex: 1,
                            outline: 'none !important',
                            padding: '10px 14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.3) !important',
                            position: 'relative',
                            zIndex: 1,
                            outline: 'none !important',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                          '&.Mui-focused': {
                            outline: 'none !important',
                            boxShadow: 'none !important',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.3) !important',
                              outline: 'none !important',
                              boxShadow: 'none !important',
                            },
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            position: 'relative',
                            zIndex: 1,
                          },
                        }}
                      >
                        {DOMAINS.map((d) => (
                          <MenuItem key={d.value} value={d.value}>
                            {t('roadmap.' + d.labelKey)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
                        {t('roadmap.ideaType')}
                      </Typography>
                      <Select
                        value={fragment.idea_type || ''}
                        onChange={(e) => handleFragmentChange(index, 'idea_type', e.target.value)}
                        renderValue={(selected) => {
                          const ideaType = IDEA_TYPES.find(it => it.value === selected)
                          return (
                            <Typography sx={{ color: 'white' }}>
                              {ideaType ? t('roadmap.' + ideaType.labelKey) : selected}
                            </Typography>
                          )
                        }}
                        sx={{
                          color: 'white',
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          borderRadius: '8px',
                          position: 'relative',
                          outline: 'none !important',
                          boxShadow: 'none !important',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.2)',
                            borderRadius: '8px',
                            zIndex: 0,
                          },
                          '& .MuiSelect-select': {
                            position: 'relative',
                            zIndex: 1,
                            outline: 'none !important',
                            padding: '10px 14px',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.3) !important',
                            position: 'relative',
                            zIndex: 1,
                            outline: 'none !important',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                          },
                          '&.Mui-focused': {
                            outline: 'none !important',
                            boxShadow: 'none !important',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(255, 255, 255, 0.3) !important',
                              outline: 'none !important',
                              boxShadow: 'none !important',
                            },
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            position: 'relative',
                            zIndex: 1,
                          },
                        }}
                      >
                        {IDEA_TYPES.map((it) => (
                          <MenuItem key={it.value} value={it.value}>
                            {t('roadmap.' + it.labelKey)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 0.5, fontSize: '0.875rem', fontWeight: 500 }}>
                      {t('roadmap.horizonLabel')}
                    </Typography>
                    <Select
                      value={fragment.horizon || 'DREAMS'}
                      onChange={(e) => handleFragmentChange(index, 'horizon', e.target.value)}
                      renderValue={(selected) => {
                        return (
                          <Typography sx={{ color: 'white' }}>
                            {getHorizonLabel(selected)}
                          </Typography>
                        )
                      }}
                      sx={{
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        borderRadius: '8px',
                        position: 'relative',
                        outline: 'none !important',
                        boxShadow: 'none !important',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                          borderRadius: '8px',
                          zIndex: 0,
                        },
                        '& .MuiSelect-select': {
                          position: 'relative',
                          zIndex: 1,
                          outline: 'none !important',
                          padding: '10px 14px',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.3) !important',
                          position: 'relative',
                          zIndex: 1,
                          outline: 'none !important',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused': {
                          outline: 'none !important',
                          boxShadow: 'none !important',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255, 255, 255, 0.3) !important',
                            outline: 'none !important',
                            boxShadow: 'none !important',
                          },
                        },
                        '& .MuiSvgIcon-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          position: 'relative',
                          zIndex: 1,
                        },
                      }}
                    >
                      {HORIZONS.map((h) => (
                        <MenuItem key={h.value} value={h.value}>
                          {getHorizonLabel(h.value)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddFragment}
              variant="outlined"
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.4)',
                color: 'white',
                position: 'relative',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                padding: '10px 16px',
                fontSize: '0.95rem',
                fontWeight: 600,
                textTransform: 'none',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '4px',
                  zIndex: 0,
                },
                '& .MuiButton-label': {
                  position: 'relative',
                  zIndex: 1,
                },
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.6)',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(255, 255, 255, 0.2)',
                },
              }}
            >
              {t('roadmap.addFragment')}
            </Button>

            {/* Parent Idea Action Selection */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1.5, fontSize: '0.875rem', fontWeight: 500 }}>
                {t('roadmap.fragmentOriginalQ')}
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={parentAction}
                  onChange={(e) => setParentAction(e.target.value)}
                  renderValue={(selected) => {
                    return (
                      <Typography sx={{ color: 'white' }}>
                        {PARENT_ACTION_KEYS[selected] ? t('roadmap.' + PARENT_ACTION_KEYS[selected]) : selected}
                      </Typography>
                    )
                  }}
                  sx={{
                    color: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '8px',
                    position: 'relative',
                    outline: 'none !important',
                    boxShadow: 'none !important',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      zIndex: 0,
                    },
                    '& .MuiSelect-select': {
                      position: 'relative',
                      zIndex: 1,
                      outline: 'none !important',
                      padding: '10px 14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3) !important',
                      position: 'relative',
                      zIndex: 1,
                      outline: 'none !important',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused': {
                      outline: 'none !important',
                      boxShadow: 'none !important',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3) !important',
                        outline: 'none !important',
                        boxShadow: 'none !important',
                      },
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      position: 'relative',
                      zIndex: 1,
                    },
                  }}
                >
                  <MenuItem value="KEEP">{t('roadmap.fragmentKeepAsIs')}</MenuItem>
                  <MenuItem value="ABANDONED">{t('roadmap.fragmentMarkAbandoned')}</MenuItem>
                  <MenuItem value="COMPLETED">{t('roadmap.fragmentMarkCompleted')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            position: 'relative',
            zIndex: 1,
            p: 2,
            gap: 1.5,
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.4)',
              color: 'white',
              position: 'relative',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              padding: '10px 16px',
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
                zIndex: 0,
              },
              '& .MuiButton-label': {
                position: 'relative',
                zIndex: 1,
              },
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.6)',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(255, 255, 255, 0.2)',
              },
            }}
          >
            {t('roadmap.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              position: 'relative',
              padding: '10px 16px',
              fontSize: '0.95rem',
              fontWeight: 600,
              textTransform: 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.15)',
                borderRadius: '4px',
                zIndex: 0,
              },
              '& .MuiButton-label': {
                position: 'relative',
                zIndex: 1,
              },
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
              },
            }}
          >
            {loading ? t('roadmap.fragmentCreating') : t('roadmap.fragmentCreate')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default FragmentIdeaDialog

